import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BackToBackLcService } from '../../services/backToBackLc.service';
import { SupplierService } from '../../services/supplier.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { PERMISSIONS } from '../../constants/permissions';
import { B2B_STATUS_COLORS, BackToBackLc, CreateBackToBackLcRequest } from '../../interfaces/commercial.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import B2bUtilizationDialog from './B2bUtilizationDialog';
import { formatMoney } from '../../utils/format';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();
const EMPTY = {
    b2bLcNumber: '', issuingBank: '', supplierName: '', currencyCode: '',
    amount: '', issueDate: today(), expiryDate: today(), latestShipmentDate: '',
};

/** Master-scoped tab shown inside ExportLcDetail — distinct from the institution-wide BackToBackLcRegister page. */
const BackToBackLcPanel: React.FC<{ masterLcId: string; masterCurrencyCode: string }> = ({ masterLcId, masterCurrencyCode }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);

    const [rows, setRows] = useState<BackToBackLc[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ...EMPTY, currencyCode: masterCurrencyCode });
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [saving, setSaving] = useState(false);
    const [utilTarget, setUtilTarget] = useState<BackToBackLc | null>(null);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await BackToBackLcService.listByMaster(institutionId, masterLcId, 0, 200);
            setRows(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load back-to-back LCs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [masterLcId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSuppliers = async () => {
        const r = await loadSuppliersSafely(() => SupplierService.listByInstitution(0, 200));
        setSuppliers(r.suppliers);
        if (r.error) toast.showError(r.error);
    };

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const openCreate = () => {
        setForm({ ...EMPTY, currencyCode: masterCurrencyCode });
        setSelectedSupplier(null);
        loadSuppliers();
        setOpen(true);
    };

    const handleCreate = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !form.supplierName.trim() || !form.b2bLcNumber.trim() || !form.amount) return;
        const dto: CreateBackToBackLcRequest = {
            b2bLcNumber: form.b2bLcNumber.trim(),
            issuingBank: form.issuingBank.trim(),
            supplierId: selectedSupplier?.id,
            supplierName: form.supplierName.trim(),
            currencyCode: form.currencyCode.trim().toUpperCase(),
            amount: Number(form.amount),
            issueDate: form.issueDate,
            expiryDate: form.expiryDate,
            latestShipmentDate: form.latestShipmentDate || undefined,
        };
        try {
            setSaving(true);
            await BackToBackLcService.create(institutionId, masterLcId, dto);
            toast.showSuccess('Back-to-back LC created');
            setOpen(false);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to create back-to-back LC');
        } finally {
            setSaving(false);
        }
    };

    /** The one manual status move: the master LC cannot be closed while any of these is open. */
    const handleClose = async (b: BackToBackLc) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        if (!window.confirm(`Close back-to-back LC "${b.b2bLcNumber}"? This cannot be undone.`)) return;
        try {
            await BackToBackLcService.update(institutionId, masterLcId, b.id, {
                masterLcId,
                b2bLcNumber: b.b2bLcNumber,
                issuingBank: b.issuingBank,
                supplierId: b.supplierId,
                supplierName: b.supplierName,
                currencyCode: b.currencyCode,
                amount: b.amount,
                issueDate: b.issueDate,
                expiryDate: b.expiryDate,
                latestShipmentDate: b.latestShipmentDate,
                status: 'CLOSED',
            });
            toast.showSuccess('Back-to-back LC closed');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to close back-to-back LC');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Back-to-Back LCs</Typography>
                {canManage && (
                    <Button data-help="commercial.b2bPanel.new" startIcon={<AddIcon />} variant="outlined" onClick={openCreate}>
                        New Back-to-Back LC
                    </Button>
                )}
            </Box>
            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.b2bPanel.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.b2bPanel.col.number">B2B LC Number</TableCell>
                                <TableCell data-help="commercial.b2bPanel.col.supplier">Supplier</TableCell>
                                <TableCell align="right" data-help="commercial.b2bPanel.col.amount">Amount</TableCell>
                                <TableCell align="right" data-help="commercial.b2bPanel.col.utilized">Utilized</TableCell>
                                <TableCell align="right" data-help="commercial.b2bPanel.col.headroom">Headroom</TableCell>
                                <TableCell data-help="commercial.b2bPanel.col.status">Status</TableCell>
                                <TableCell align="right" data-help="commercial.b2bPanel.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(b => (
                                <TableRow key={b.id} hover>
                                    <TableCell>{b.b2bLcNumber}</TableCell>
                                    <TableCell>{b.supplierName}</TableCell>
                                    <TableCell align="right">{formatMoney(b.amount, b.currencyCode)}</TableCell>
                                    <TableCell align="right">{formatMoney(b.utilizedAmount, b.currencyCode)}</TableCell>
                                    <TableCell align="right">{formatMoney(b.remainingHeadroom, b.currencyCode)}</TableCell>
                                    <TableCell><Chip size="small" label={enumLabel(b.status)} color={B2B_STATUS_COLORS[b.status]} /></TableCell>
                                    <TableCell align="right">
                                        <Button data-help="commercial.b2bPanel.utilizations" size="small" onClick={() => setUtilTarget(b)}>Utilizations</Button>
                                        <Button data-help="commercial.b2bPanel.acceptances" size="small" onClick={() => navigate(`/commercial/back-to-back-lc/${masterLcId}/${b.id}`)}>Acceptances</Button>
                                        {canManage && b.status !== 'CLOSED' && (
                                            <Button data-help="commercial.b2bPanel.close" size="small" color="warning" onClick={() => handleClose(b)}>Close</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center">No back-to-back LCs against this master.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.b2bPanel.dialog.page' } as any}>
                <DialogTitle>New Back-to-Back LC</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.b2bPanel.number" label="B2B LC Number" fullWidth required value={form.b2bLcNumber} onChange={set('b2bLcNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.b2bPanel.bank" label="Issuing Bank" fullWidth required value={form.issuingBank} onChange={set('issuingBank')} />
                        </Grid>
                        <Grid size={12}>
                            <Autocomplete
                                options={suppliers}
                                getOptionLabel={(s) => s.name}
                                value={selectedSupplier}
                                onChange={(_e, v) => {
                                    setSelectedSupplier(v);
                                    if (v) setForm(f => ({ ...f, supplierName: v.name }));
                                }}
                                renderInput={(params) => <TextField {...params} data-help="commercial.b2bPanel.supplierPick" label="Supplier (validated reference, optional)" />}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                data-help="commercial.b2bPanel.supplierName" label="Supplier Name (required)" fullWidth required
                                value={form.supplierName} onChange={set('supplierName')}
                                helperText="Name as it should print; you can edit it after picking a supplier."
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.b2bPanel.currency" label="Currency Code" fullWidth required inputProps={{ maxLength: 3 }}
                                value={form.currencyCode} onChange={set('currencyCode')}
                                helperText="Must match the master LC's currency." />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.b2bPanel.amount" label="Amount" type="number" fullWidth required value={form.amount} onChange={set('amount')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.b2bPanel.issue" label="Issue Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.issueDate} onChange={set('issueDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.b2bPanel.expiry" label="Expiry Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.expiryDate} onChange={set('expiryDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.b2bPanel.shipment" label="Latest Shipment Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                                value={form.latestShipmentDate} onChange={set('latestShipmentDate')} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.b2bPanel.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.b2bPanel.create" variant="contained" disabled={saving} onClick={handleCreate}>
                        {saving ? 'Saving…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {utilTarget && (
                <B2bUtilizationDialog
                    open={!!utilTarget}
                    onClose={() => setUtilTarget(null)}
                    masterLcId={masterLcId}
                    b2b={utilTarget}
                    onSaved={load}
                />
            )}
        </Box>
    );
};

export default BackToBackLcPanel;
